import { FC, useMemo } from "react";
import { IWidgetTemplate } from "../../../interfaces/widget-template";
import { ReachClick } from "../../../utils/core";
import { resolveWidgetV2Style } from "./widgetV2Styles";

/**
 * Licence Badge (v2) — admin-preview-only. Note: for templateId !== 14 the
 * `company` prop comes from getCompanyInfoByIdApi (ICompanyInfo), which
 * doesn't actually populate `licenses` at runtime despite the intersected
 * ICompanyType typing — so this preview may show "No licences" even for a
 * licensed company. The real embed (widget-v2.js, renderLicence) fetches
 * from GET /widgets/information/company, which does return real licenses.
 */
const WidgetTemplate22: FC<IWidgetTemplate> = ({ company, style }) => {
  const companyId = company?.companyId;
  const resolvedStyle = resolveWidgetV2Style("licence", style);
  const payload = useMemo(() => ({ companyId, widgetId: 22 as const }), [companyId]);
  const profileUrl = useMemo(
    () => `https://www.trustfinance.com/companies/${companyId}?utm_source=widget&utm_medium=dom&utm_campaign=widget-v2-licence`,
    [companyId],
  );
  const licenses = company?.licenses ?? [];

  if (resolvedStyle === "verified-card") {
    const sub = licenses.length ? `${licenses.length} verified licence${licenses.length === 1 ? "" : "s"}` : "Verification pending";
    return (
      <a
        href={profileUrl}
        target="_blank"
        rel="noopener"
        onClick={() => ReachClick(payload)}
        className="d-block"
        style={{
          textDecoration: "none",
          color: "#1c1c2e",
          borderRadius: 8,
          padding: 1.5,
          background: "linear-gradient(135deg,#006bf1,#69adff)",
          maxWidth: 260,
          margin: "0 auto",
        }}
      >
        <span className="d-flex align-items-center gap-3" style={{ background: "#fff", borderRadius: 7, padding: "12px 16px" }}>
          <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="#006bf1" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
          </svg>
          <span>
            <b style={{ fontSize: 13.5 }}>Verified Business</b>
            <span className="d-block" style={{ fontSize: 11, color: "#6b6b85" }}>{sub}</span>
          </span>
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#f5a623" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="ms-auto">
            <path d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526" />
            <circle cx={12} cy={8} r={6} />
          </svg>
        </span>
      </a>
    );
  }

  return (
    <a
      href={profileUrl}
      target="_blank"
      rel="noopener"
      onClick={() => ReachClick(payload)}
      className="d-flex flex-wrap justify-content-center gap-2"
      style={{ textDecoration: "none", color: "#1c1c2e" }}
    >
      {licenses.length ? (
        licenses.map((l) => (
          <span
            key={l.id}
            className="d-inline-flex align-items-center gap-1"
            style={{ border: "1px solid #e7e8ec", borderRadius: 8, padding: "6px 10px", fontSize: 12, fontWeight: 600 }}
          >
            {l.shortName || l.name}
            <b style={{ borderRadius: 4, padding: "1px 6px", fontSize: 11, background: "#69adff", color: "#fff" }}>✓</b>
          </span>
        ))
      ) : (
        <span style={{ fontSize: 11, color: "#9aa0b4" }}>No verified licences yet</span>
      )}
    </a>
  );
};

export default WidgetTemplate22;
